<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Analise de Mensagens',
  },
});

const total = computed(() => props.data?.total || 0);
const incoming = computed(() => props.data?.incoming || 0);
const outgoing = computed(() => props.data?.outgoing || 0);

const incomingPercentage = computed(() => {
  if (total.value === 0) return 50;
  return ((incoming.value / total.value) * 100).toFixed(1);
});

const outgoingPercentage = computed(() => {
  if (total.value === 0) return 50;
  return ((outgoing.value / total.value) * 100).toFixed(1);
});

function formatNumber(num) {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-message-circle text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Total messages -->
      <div class="text-center pb-4 border-b border-n-weak">
        <div class="text-3xl font-bold text-n-slate-12">
          {{ formatNumber(total) }}
        </div>
        <div class="text-sm text-n-slate-11">mensagens totais</div>
      </div>

      <!-- Incoming vs Outgoing bar -->
      <div>
        <div class="flex justify-between text-xs text-n-slate-11 mb-2">
          <span>Recebidas</span>
          <span>Enviadas</span>
        </div>
        <div class="flex h-4 rounded-full overflow-hidden bg-n-alpha-1">
          <div
            class="bg-n-blue-9 transition-all duration-300"
            :style="{ width: `${incomingPercentage}%` }"
          />
          <div
            class="bg-n-teal-9 transition-all duration-300"
            :style="{ width: `${outgoingPercentage}%` }"
          />
        </div>
        <div class="flex justify-between mt-2">
          <div class="flex items-center gap-2">
            <span class="w-3 h-3 rounded-full bg-n-blue-9" />
            <span class="text-sm font-medium text-n-blue-11">{{
              formatNumber(incoming)
            }}</span>
            <span class="text-xs text-n-slate-11">({{ incomingPercentage }}%)</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-n-slate-11">({{ outgoingPercentage }}%)</span>
            <span class="text-sm font-medium text-n-teal-11">{{
              formatNumber(outgoing)
            }}</span>
            <span class="w-3 h-3 rounded-full bg-n-teal-9" />
          </div>
        </div>
      </div>

      <!-- Additional metrics -->
      <div class="grid grid-cols-2 gap-3 pt-4 border-t border-n-weak">
        <div class="text-center p-2 rounded-lg bg-n-alpha-1">
          <div class="text-lg font-bold text-n-violet-11">
            {{ formatNumber(data?.private_notes || 0) }}
          </div>
          <div class="text-xs text-n-slate-11">Notas privadas</div>
        </div>
        <div class="text-center p-2 rounded-lg bg-n-alpha-1">
          <div class="text-lg font-bold text-n-amber-11">
            {{ formatNumber(data?.with_attachments || 0) }}
          </div>
          <div class="text-xs text-n-slate-11">Com anexos</div>
        </div>
        <div class="text-center p-2 rounded-lg bg-n-alpha-1">
          <div class="text-lg font-bold text-n-slate-12">
            {{ data?.avg_incoming_length || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Tam. medio (rec.)</div>
        </div>
        <div class="text-center p-2 rounded-lg bg-n-alpha-1">
          <div class="text-lg font-bold text-n-slate-12">
            {{ data?.avg_outgoing_length || 0 }}
          </div>
          <div class="text-xs text-n-slate-11">Tam. medio (env.)</div>
        </div>
      </div>

      <!-- Message ratio -->
      <div
        class="flex items-center justify-between pt-3 border-t border-n-weak"
      >
        <span class="text-sm text-n-slate-11">Taxa de resposta</span>
        <span class="text-sm font-medium text-n-slate-12">
          {{ data?.message_ratio || 0 }} msg/resposta
        </span>
      </div>
    </div>
  </div>
</template>
